/**
 * RSI Divergence Detector
 *
 * Compares RSI swing pivots with price swing pivots to detect
 * bullish and bearish divergences.
 *
 * Category: Advanced | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeEMA } from "./sma";

function computeRSI(closes: number[], period: number): (number | undefined)[] {
  const rsi: (number | undefined)[] = new Array(closes.length).fill(undefined);
  let avgGain = 0, avgLoss = 0;

  for (let i = 1; i <= period && i < closes.length; i++) {
    const change = closes[i] - closes[i - 1];
    if (change > 0) avgGain += change;
    else avgLoss += Math.abs(change);
  }

  avgGain /= period;
  avgLoss /= period;

  if (period < closes.length) {
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    rsi[period] = 100 - 100 / (1 + rs);
  }

  for (let i = period + 1; i < closes.length; i++) {
    const change = closes[i] - closes[i - 1];
    avgGain = (avgGain * (period - 1) + Math.max(change, 0)) / period;
    avgLoss = (avgLoss * (period - 1) + Math.max(-change, 0)) / period;
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    rsi[i] = 100 - 100 / (1 + rs);
  }
  return rsi;
}

export const rsiDivergenceIndicator: IndicatorDefinition = {
  id: "rsi_divergence",
  name: "RSI Divergence",
  shortName: "RSIDiv",
  category: "advanced",
  pane: "separate",
  params: [
    { key: "rsiPeriod", label: "RSI Period", type: "number", default: 14, min: 5, max: 50 },
    { key: "pivotLookback", label: "Pivot Lookback", type: "number", default: 5, min: 2, max: 20 },
  ],
  plots: [
    { key: "rsi", label: "RSI", color: "#AB47BC", style: "line", lineWidth: 2 },
    { key: "overbought", label: "OB", color: "#555555", style: "line", lineWidth: 1 },
    { key: "oversold", label: "OS", color: "#555555", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { rsiPeriod, pivotLookback } = params;
    const closes = candles.map(c => c.close);
    const rsi = computeRSI(closes, rsiPeriod);

    const rsiData: { time: number; value: number }[] = [];
    const obData: { time: number; value: number }[] = [];
    const osData: { time: number; value: number }[] = [];

    for (let i = 0; i < candles.length; i++) {
      if (rsi[i] !== undefined) {
        rsiData.push({ time: candles[i].time, value: rsi[i]! });
        obData.push({ time: candles[i].time, value: 70 });
        osData.push({ time: candles[i].time, value: 30 });
      }
    }

    return [
      { key: "rsi", data: rsiData },
      { key: "overbought", data: obData },
      { key: "oversold", data: osData },
    ];
  },
};
